import React, { useState, useEffect } from 'react';
import styles from './Header.module.css';
import { RiMenuLine } from 'react-icons/ri';
import { BsChatLeft, BsClockHistory } from 'react-icons/bs';
import { IoSettingsOutline } from 'react-icons/io5';
import { IconButton, Menu, MenuItem, Avatar, Tooltip, Divider } from '@mui/material';
import { FaGoogle } from 'react-icons/fa';
import ChatHistory from '../ChatHistory/ChatHistory';
import ConversationHistory from '../ConversationHistory/ConversationHistory';
import CanvasList from '../CanvasList/CanvasList';
import Settings from '../Settings/Settings';
import { supabase } from '../../supabaseClient';
import historyManager from '../../services/historyManager';

const Header = ({ 
  onCanvasListToggle, 
  onConversationSelect, 
  onChatHistoryToggle, 
  onSettingsToggle,
  isChatHistoryOpen,
  isSettingsOpen,
  justUpdatedConversationId,
  isSplitViewEnabled
}) => {
  const [isCanvasListOpen, setIsCanvasListOpen] = useState(false);
  const [isConversationHistoryOpen, setIsConversationHistoryOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Add ESC key handler for conversation history
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isConversationHistoryOpen) {
        setIsConversationHistoryOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isConversationHistoryOpen]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://hapwrcoabbackocyuksu.supabase.co/auth/v1/callback'
        }
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error signing in:', error.message);
    }
  };

  const handleSignOut = async () => {
    try {
      localStorage.clear();
      historyManager.clear();
      window.dispatchEvent(new CustomEvent('userSignedOut'));
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error.message);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleConversationSelect = (conversation) => {
    if (!conversation) {
      console.log('No conversation data, skipping');
      return;
    }
    setSelectedConversation(conversation);
    setIsConversationHistoryOpen(false);
    onConversationSelect?.(conversation);
  };

  // Handle logo/title click to reset everything
  const handleBrandClick = () => {
    console.log('Brand clicked, resetting application state');
    // Clear conversation selection
    setSelectedConversation(null);
    onConversationSelect?.(null);
    // Close all sidebars
    setIsConversationHistoryOpen(false);
    setIsCanvasListOpen(false);
    onSettingsToggle?.(false);
    onChatHistoryToggle?.(false);
    // Clear history manager
    historyManager.clear();
    
    // Dispatch custom event to clear active conversation indication
    window.dispatchEvent(new CustomEvent('clearActiveConversation'));
  };

  return (
    <>
    <header className={styles.header}>
      <div className={styles.leftSection}>
        <IconButton 
          size="small" 
          title="Conversation History"
          onClick={() => setIsConversationHistoryOpen(true)}
          className={styles.leftmostButton}
          aria-label="Conversation History"
        >
          <BsClockHistory size={20} />
        </IconButton>
      </div>

      <div 
        className={`${styles.brandContainer} ${styles.clickable}`}
        onClick={handleBrandClick}
        role="button"
        tabIndex={0}
        title="Take Home"
        aria-label="Pralok Assistant - Take Home"
      >
        <img 
          src="/logo.svg" 
          alt="Pralok Logo" 
          className={styles.logo}
          width="38"
          height="38"
        />
        <div className={styles.brandInfo}>
          <h1 className={styles.brandName}>Pralok Assistant</h1>
          <span className={styles.motto}>Discuss • Learn • Solve</span>
        </div>
      </div>

      <div className={styles.rightSection}>
        <IconButton 
          size="small" 
          title="Canvas List"
          onClick={onCanvasListToggle}
          aria-label="Canvas List"
        >
          <RiMenuLine size={20} />
        </IconButton>
        {/* Only show chat history button when split view is disabled */}
        {!isSplitViewEnabled && (
          <IconButton 
            size="small" 
            title="Messages"
            onClick={() => onChatHistoryToggle?.()}
            aria-label="Messages"
          >
            <BsChatLeft size={20} />
          </IconButton>
        )}
        {!user ? (
          <Tooltip title="Sign in with Google">
            <IconButton
              size="small"
              onClick={handleSignIn}
              className={styles.authButton}
              aria-label="Sign in with Google"
            >
              <FaGoogle size={18} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Settings & Account">
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              className={styles.settingsButton}
              aria-label="Settings & Account"
            >
              <Avatar
                src={user.user_metadata?.avatar_url || ''}
                alt={user.email}
                className={styles.avatar}
              >
                {user.email[0].toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
        )}
      </div>
    </header>

    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      className={styles.settingsMenu}
    >
      <MenuItem onClick={() => {
        handleMenuClose();
        onSettingsToggle?.();
      }}>
        Settings
      </MenuItem>
      <Divider />
      <MenuItem onClick={() => {
        handleSignOut();
        handleMenuClose();
      }}>
        Sign Out
      </MenuItem>
    </Menu>

    <CanvasList
      isOpen={isCanvasListOpen}
      onClose={() => setIsCanvasListOpen(false)}
      conversation={selectedConversation}
    />

    <ChatHistory 
      isOpen={isChatHistoryOpen}
      onClose={() => onChatHistoryToggle?.()}
    />

    <ConversationHistory
      isOpen={isConversationHistoryOpen}
      onClose={() => setIsConversationHistoryOpen(false)}
      onConversationSelect={handleConversationSelect}
      justUpdatedConversationId={justUpdatedConversationId}
    />

    <Settings
      isOpen={isSettingsOpen}
      onClose={() => onSettingsToggle?.()}
    />
    </>
  );
};

export default Header;
